import React, { useState } from 'react';
import Wrapper from './styles';
import { toast } from 'react-toastify';
import tunisiaLocations from '../../data/tunisia-locations.json';
import axios from 'axios';
import { create_order_url } from '../../utils/constants';
import { formatPrice } from '../../utils/helpers';
import { setPixelUserData, buildContents, sumItems, metaOrderFields } from '../../utils/metaPixel';

// Tunisia-only location data extracted from the `country-state-city` package
// (see src/data/tunisia-locations.json). That package ships a 25MB global
// city database that was getting bundled in full just for this one country,
// bloating the production JS bundle by ~18MB for data we never used.
const countries = [tunisiaLocations.country];
const states = tunisiaLocations.states;
const cities = tunisiaLocations.cities;

const CashOnDeliveryForm = ({ product, amount, selectedColor, selectedSize, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone_number: '',
    line1: '',
    postal_code: '',
    city: '',
    state: '',
    country: 'TN',
  });
  const [loading, setLoading] = useState(false);

  const itemsPrice = product.price * amount;
  const shipping_fee = (product.shipping && itemsPrice < 200) ? 7 : 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const zipRegex = new RegExp('^[0-9]{4}$');

    if (!formData.firstName) {
      return toast.error('Enter your First Name');
    }
    if (!formData.lastName) {
      return toast.error('Enter your Last Name');
    }
    if (!formData.phone_number || formData.phone_number.length < 8) {
      return toast.error('Enter a valid phone number');
    }
    if (!formData.line1) {
      return toast.error('Enter your Address');
    }
    if (!formData.postal_code) {
      return toast.error('Enter your Zip Code');
    }
    if (!zipRegex.test(formData.postal_code)) {
      return toast.error('Enter Valid Zip Code');
    }
    if (!formData.city) {
      return toast.error('Select your City');
    }
    if (!formData.state) {
      return toast.error('Select your State');
    }
    if (!formData.country) {
      return toast.error('Select your Country');
    }

    setLoading(true);

    const shippingInfo = {
      address: formData.line1,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      pinCode: formData.postal_code,
      phoneNumber: formData.phone_number,
    };

    const orderItems = [
      {
        name: product.name,
        price: product.price,
        quantity: amount,
        image: product.images[0]?.url || '',
        color: selectedColor,
        size: selectedSize,
        product: product._id,
      },
    ];

    const paymentInfo = {
      id: 'COD_' + Date.now(),
      status: 'Cash on Delivery',
    };

    const itemsPrice = product.price * amount;
    const totalPrice = itemsPrice + shipping_fee;
    const eventId = "order_" + Date.now();

    const body = {
      name: `${formData.firstName} ${formData.lastName}`,
      email: `guest_${Date.now()}@cashondelivery.com`, // Guest email for COD orders
      shippingInfo,
      orderItems,
      paymentInfo,
      itemsPrice,
      shippingPrice: shipping_fee,
      totalPrice,
      eventId, // Pass eventId to backend for Meta CAPI deduplication
      ...metaOrderFields(), // fbp/fbc/eventSourceUrl for the Conversions API
    };

    try {
      await axios.post(create_order_url, body);

      // Track Purchase with Meta Pixel (Advanced Matching + per-item prices)
      if (window.fbq) {
        setPixelUserData({
          name: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
          phone: formData.phone_number,
          city: formData.city,
          state: formData.state,
          zip: formData.postal_code,
        });
        const contents = buildContents(
          orderItems.map((i) => ({ id: i.product, price: i.price, quantity: i.quantity }))
        );
        window.fbq('track', 'Purchase', {
          value: Number(totalPrice) || 0,
          currency: 'TND',
          content_ids: contents.map((c) => c.id),
          content_type: 'product',
          contents,
          num_items: sumItems(contents),
        }, {
          eventID: eventId
        });
      }

      toast.success('Order placed successfully! You will pay on delivery.');
      setLoading(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error('Failed to place order. Please try again.');
      setLoading(false);
    }
  };

  const totalPrice = itemsPrice + shipping_fee;

  return (
    <Wrapper>
      <div className='form-container'>
        <div className='title'>
          <h3>Cash on Delivery</h3>
          <p>Fill in your details to place your order</p>
        </div>

        <div className='order-summary'>
          <h4>Order Summary</h4>
          <p><span>Product:</span> {product.name}</p>
          <p><span>Quantity:</span> {amount}</p>
          <p><span>Color:</span> <span className='color-box' style={{ backgroundColor: selectedColor }}></span></p>
          <p><span>Size:</span> {selectedSize}</p>
          <p><span>Price:</span> {formatPrice(itemsPrice)}</p>
          <p><span>Shipping:</span> {formatPrice(shipping_fee)}</p>
          <hr />
          <p className='total'><span>Total:</span> {formatPrice(totalPrice)}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className='form-row'>
            <div className='form-control'>
              <input
                type='text'
                name='firstName'
                className='input'
                placeholder='First Name'
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            <div className='form-control'>
              <input
                type='text'
                name='lastName'
                className='input'
                placeholder='Last Name'
                value={formData.lastName}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className='form-control'>
            <input
              type='tel'
              name='phone_number'
              className='input'
              placeholder='Phone Number (e.g., 21612345678)'
              value={formData.phone_number}
              onChange={handleChange}
            />
          </div>

          <div className='form-control'>
            <input
              type='text'
              name='line1'
              className='input'
              placeholder='Address'
              value={formData.line1}
              onChange={handleChange}
            />
          </div>

          <div className='form-control'>
            <input
              type='number'
              name='postal_code'
              className='input'
              placeholder='Zip Code'
              value={formData.postal_code}
              onChange={handleChange}
            />
          </div>

          <div className='form-control'>
            <select
              name='city'
              className='input sort-input'
              value={formData.city}
              onChange={handleChange}
            >
              <option value=''>Select City</option>
              {cities.map((item, index) => (
                <option key={index} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className='form-control'>
            <select
              name='state'
              className='input sort-input'
              value={formData.state}
              onChange={handleChange}
            >
              <option value=''>Select State</option>
              {states.map((item, index) => (
                <option key={index} value={item.name}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className='form-control'>
            <select
              name='country'
              className='input sort-input'
              value={formData.country}
              onChange={handleChange}
            >
              <option value=''>Select Country</option>
              {countries.map((item, index) => (
                <option key={index} value={item.isoCode}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className='btn-container'>
            <button type='submit' className='btn confirm-btn' disabled={loading}>
              {loading ? 'Placing Order...' : 'Confirm Order (Pay on Delivery)'}
            </button>
            <button type='button' className='btn cancel-btn' onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Wrapper>
  );
};

export default CashOnDeliveryForm;
